import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { ArrowLeft, Trophy, Target, Percent } from "lucide-react";

export default function PlayerProfilePage() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [imgOk, setImgOk] = useState(true);
  useEffect(() => { api.get(`/players/${id}`).then((r) => setP(r.data)).catch(() => setP(false)); }, [id]);

  if (p === null) return <div className="p-20 text-center">Loading…</div>;
  if (p === false) return <div className="p-20 text-center">Player not found. <Link to="/players" className="text-jpa-primary underline">Back</Link></div>;

  const winRate = p.win_rate ?? (p.wins + p.losses > 0 ? Math.round((p.wins / (p.wins + p.losses)) * 100) : 0);

  const photoUrl = p.jpa_id && imgOk ? `${process.env.REACT_APP_BACKEND_URL}/api/players/by-jpa/${p.jpa_id}/photo` : null;
  const hideOnError = () => setImgOk(false);

  return (
    <div data-testid={`player-profile-${id}`}>
      <section className="relative bg-jpa-ink text-white overflow-hidden">
        {photoUrl && <img src={photoUrl} alt="" onError={hideOnError} className="absolute inset-0 w-full h-full object-cover opacity-30" />}
        <div className="absolute inset-0 bg-gradient-to-r from-jpa-ink via-jpa-ink/95 to-jpa-ink/60" />
        <div className="relative max-w-[1400px] mx-auto px-4 md:px-8 py-16 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <div className="aspect-[3/4] overflow-hidden border-4 border-jpa-accent bg-jpa-surface flex items-center justify-center">
              {photoUrl ? (
                <img src={photoUrl} alt={p.name} onError={hideOnError} loading="lazy" className="w-full h-full object-cover" />
              ) : (
                <div data-testid={`player-photo-pending-${id}`} className="text-center px-4">
                  <div className="font-display text-2xl tracking-wider text-jpa-muted">PHOTO</div>
                  <div className="font-display text-2xl tracking-wider text-jpa-muted">PENDING</div>
                </div>
              )}
            </div>
          </div>
          <div className="lg:col-span-8">
            <Link to="/players" className="inline-flex items-center gap-2 text-sm uppercase tracking-widest text-white/70 hover:text-white mb-3">
              <ArrowLeft className="w-4 h-4" /> All players
            </Link>
            <span className="bg-jpa-accent text-jpa-ink px-3 py-1 text-xs font-bold uppercase tracking-widest skew-badge">
              Rank #{p.ranking || "—"} • {p.gender === "M" ? "Men's" : "Women's"}
            </span>
            {p.jpa_id && (
              <span className="ml-2 bg-white/10 border border-white/20 text-white px-3 py-1 text-xs font-bold uppercase tracking-widest skew-badge" data-testid={`player-jpa-id-${id}`}>
                {p.jpa_id}
              </span>
            )}
            <h1 className="font-display text-6xl md:text-8xl tracking-wider mt-4">{p.name}</h1>
            <p className="mt-2 text-white/70 uppercase tracking-widest text-sm">{p.district}{p.club ? ` • ${p.club}` : ""}</p>
            {p.eligible_age_labels?.length > 0 && (
              <div className="mt-4 flex flex-wrap items-center gap-2" data-testid={`player-eligible-ages-${id}`}>
                <span className="text-[10px] font-bold uppercase tracking-widest text-white/60">Eligible age categories:</span>
                {p.eligible_age_labels.map((lbl) => (
                  <span key={lbl} className="bg-jpa-primary/80 text-white px-2 py-1 text-[10px] font-bold uppercase tracking-widest skew-badge">{lbl}</span>
                ))}
              </div>
            )}
            <p className="mt-6 text-white/85 max-w-2xl leading-relaxed">{p.bio}</p>

            <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { l: "Points", v: p.points, i: Trophy },
                { l: "Titles", v: p.titles, i: Trophy },
                { l: "Wins", v: `${p.wins}-${p.losses}`, i: Target },
                { l: "Win rate", v: `${winRate}%`, i: Percent },
              ].map((s) => (
                <div key={s.l} className="border border-white/15 p-4">
                  <s.i className="w-4 h-4 text-jpa-accent mb-2" />
                  <div className="font-display text-4xl tracking-wider">{s.v}</div>
                  <div className="text-xs uppercase tracking-widest text-white/60">{s.l}</div>
                </div>
              ))}
            </div>

            {p.category_stats && p.category_stats.length > 0 && (
              <div className="mt-10">
                <div className="text-[10px] font-bold uppercase tracking-widest text-white/60 mb-3">Stats by category</div>
                <div className="border border-white/15 overflow-hidden">
                  <div className="grid grid-cols-12 bg-white/5 py-2 px-3 text-[10px] font-bold uppercase tracking-widest text-white/70">
                    <div className="col-span-4">Category</div>
                    <div className="col-span-2 text-right">Pts</div>
                    <div className="col-span-2 text-right">Titles</div>
                    <div className="col-span-2 text-right">W-L</div>
                    <div className="col-span-2 text-right">Win %</div>
                  </div>
                  {p.category_stats.map((c) => (
                    <div key={`${c.event_category}__${c.age_category}`} data-testid={`cat-stat-${c.event_category}-${c.age_category}`} className="grid grid-cols-12 py-2 px-3 text-sm border-t border-white/10">
                      <div className="col-span-4 font-bold uppercase truncate">{c.event_label} · {c.age_label}</div>
                      <div className="col-span-2 text-right">{c.points}</div>
                      <div className="col-span-2 text-right">{c.titles}</div>
                      <div className="col-span-2 text-right">{c.wins}-{c.losses}</div>
                      <div className="col-span-2 text-right">{c.win_rate}%</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
